
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Gift, CreditCard, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { RedemptionOptionsConfig } from '@/types/redemptionTypes';

const TremendousSettingsTab = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [redemptionConfig, setRedemptionConfig] = useState<RedemptionOptionsConfig>({
    enableCashRedemption: true,
    enableGiftCardRedemption: true,
    enablePromoCouponRedemption: false,
    cashRedemptionMinAmount: 10,
    cashRedemptionMaxAmount: 1000,
    cashPointValue: 0.01,
    giftCardPrefix: 'GC',
    giftCardMinAmount: 5,
    giftCardMaxAmount: 500,
    giftCardPointValue: 0.01,
    promoCodeFormat: 'CODE139',
    promoCodeMinAmount: 5,
    promoCodeMaxAmount: 100,
    promoCodePointValue: 0.02
  });

  const handleToggleChange = (field: keyof RedemptionOptionsConfig) => {
    setRedemptionConfig(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleValueChange = (field: keyof RedemptionOptionsConfig, value: string | number) => {
    setRedemptionConfig(prev => ({
      ...prev,
      [field]: typeof prev[field] === 'number' ? Number(value) : value
    }));
  };

  const handleTestConnection = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      toast({
        title: 'Connection Successful',
        description: 'Tremendous API connection verified successfully.',
      });
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: 'Could not connect to Tremendous API. Please check your credentials.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: 'Settings Saved',
        description: 'Redemption settings have been updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Failed to Save',
        description: 'There was an error saving your settings.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Tremendous API Connection</CardTitle>
          <CardDescription>
            Configure your Tremendous API integration for gift card rewards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between border p-4 rounded-md bg-muted/50">
            <div className="flex items-center gap-3">
              <Gift className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-semibold">API Connection Status</h3>
                <p className="text-sm text-muted-foreground">
                  Tremendous API is configured via environment variables
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleTestConnection}
              disabled={isLoading}
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Test Connection
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Gift Card Redemption Settings</CardTitle>
          <CardDescription>
            Configure how users can redeem their points for gift cards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enable-giftcard" className="text-base font-medium">
                  Enable Gift Card Redemptions
                </Label>
                <p className="text-sm text-muted-foreground">
                  Allow users to redeem points for gift cards
                </p>
              </div>
              <Switch
                id="enable-giftcard"
                checked={redemptionConfig.enableGiftCardRedemption}
                onCheckedChange={() => handleToggleChange('enableGiftCardRedemption')}
              />
            </div>
            
            {redemptionConfig.enableGiftCardRedemption && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                <div className="space-y-2">
                  <Label htmlFor="giftcard-min">Minimum Gift Card Amount</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="giftcard-min"
                      type="number"
                      value={redemptionConfig.giftCardMinAmount}
                      onChange={(e) => handleValueChange('giftCardMinAmount', e.target.value)}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="giftcard-max">Maximum Gift Card Amount</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="giftcard-max"
                      type="number"
                      value={redemptionConfig.giftCardMaxAmount}
                      onChange={(e) => handleValueChange('giftCardMaxAmount', e.target.value)}
                      className="rounded-l-none"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="giftcard-point-value">Point Value ($ per point)</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="giftcard-point-value"
                      type="number"
                      value={redemptionConfig.giftCardPointValue}
                      onChange={(e) => handleValueChange('giftCardPointValue', e.target.value)}
                      step="0.01"
                      min="0.001"
                      max="1"
                      className="rounded-l-none"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Example: 0.01 means 100 points = $1
                  </p>
                </div>
              </div>
            )}
          </div>

          <Button 
            onClick={handleSaveSettings} 
            className="w-full md:w-auto"
            disabled={isLoading}
          >
            Save Redemption Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default TremendousSettingsTab;
